<script setup lang="ts">
import { RouteName } from '@/router/models/route-name';
import { entraAuthService } from '@/services/auth/entra-auth-service';
import { PhList, PhPower } from '@phosphor-icons/vue';
import { UserRole } from 'cesg-shared/dist/models';
import { ref, useTemplateRef } from 'vue';
import  { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { isLoggedIn, me } = entraAuthService;

const { t } = useI18n();

const route = useRoute();
const dropDownMenu = useTemplateRef('dropdown');

onClickOutside(dropDownMenu, () => isDropdownOpen.value = false);

const adminRoutes = [
  RouteName.AdminProjectAndUsers,
  RouteName.Invoices,
  RouteName.AdminSupplierInvoices,
  RouteName.AdminSuppliers,
  RouteName.AuthTest,
]

const isActive = (tabs: RouteName[]) => {
  if (!route.name) return false;
  return tabs.some((tab) => route.name === tab);
};

const isDropdownOpen = ref(false);

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
}

const logout = async () => {
  await entraAuthService.logout();
};
</script>

<template>
  <header class="hero is-medium">
    <div class="hero-head">
      <div class="container is-fluid">
        <nav class="navbar">
          <div class="navbar-brand">
            <router-link :to="{ name: RouteName.Projects }" class="logo-link">
              <img src="@/assets/logo/CESG.svg" alt="Logo" width="100" height="30" />
            </router-link>
          </div>

          <div class="navbar-menu">
            <div class="navbar-end">
              <div class="navbar-item has-dropdown" v-if="isLoggedIn && me?.role === UserRole.ADMIN"
                :class="{ 'is-active': isDropdownOpen }">
                <a class="navbar-link" @click="toggleDropdown">
                  <PhList size="24" weight="bold" />
                </a>
                <div class="navbar-dropdown" ref="dropdown">
                  <router-link class="navbar-item" :to="{ name: RouteName.AdminProjectAndUsers }" :class="{
                    'is-active': isActive(adminRoutes),
                  }" @click="isDropdownOpen = false">
                    {{ t('navigation.menu.admin') }}
                  </router-link>
                  <router-link class="navbar-item" :to="{ name: RouteName.Projects }"
                    :class="{ 'is-active': isActive([RouteName.Projects]) }" @click="isDropdownOpen = false">
                    {{ t('navigation.menu.project') }}
                  </router-link>
                  <hr class="navbar-divider" />
                  <a class="navbar-item" @click="logout">{{
                    t('navigation.menu.logout')
                  }}</a>
                </div>
              </div>

              <div class="navbar-item" v-if="isLoggedIn && me?.role !== UserRole.ADMIN">
                <button class="button is-text is-rounded" @click="logout">
                  <PhPower :size="24" weight="bold"></PhPower>
                </button>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
@use '@/assets/style/variables';

header {
  background-color: var(--header-background-color);
  border-bottom: 2px solid rgba(#000, 0.1);
  box-shadow: 0 0.5rem 1rem rgba(#000, 0.03);
}

a.navbar-item.is-active {
  color: white;
  background-color: var(--primary-color);
}
a.navbar-item.is-active:hover {
  color: var(--admin-suppliers-action-grey-text);
}

a.navbar-item:hover {
  background-color: transparent;
}

.navbar-link::after {
  display: none;
}

.logo-link {
  display: flex;
  align-items: center;
  cursor: pointer;
}
</style>
